/* eslint-disable import/no-cycle */
import IResponseModel from '@/models/interfaces/response.d';
import ValidationResultType from '@/enums/ValidationResultType';
import RegexExpressions from '@/RegexExpressions';
import ValidationResultModel from '@/models/ValidationResultModel';
import ValidationLevelType from '@/enums/ValidationLevelType';
import { lookupService } from '@/services/LookupService';

class ValidationApiService {
  private static serviceInstance: ValidationApiService;

  public static get instance() {
    // eslint-disable-next-line no-return-assign
    return this.serviceInstance || (this.serviceInstance = new this());
  }

  private umbracoValidationApiDomain: string = `${process.env.VUE_APP_API_DOMAIN}/umbraco/api`;

  public isPhoneNumberValidRegex(phoneNumber: string): boolean {
    if (phoneNumber == null || phoneNumber.length < 1) {
      return false;
    }

    if (this.phoneNumberIsInvalidLength(phoneNumber)) {
      return false;
    }

    if (this.telephoneContainsLetters(phoneNumber)) {
      return false;
    }

    return true;
  }

  public async isPhoneNumberValid(phoneNumber: string, useEnhancedValidation: boolean = false): Promise<boolean> {
    if (!this.isPhoneNumberValidRegex(phoneNumber)) {
      return false;
    }
    if (useEnhancedValidation) {
      const activePhoneCheck = await lookupService.activePhoneNumberCheck(phoneNumber);

      return activePhoneCheck && activePhoneCheck.isValid;
    }

    const basicPhoneCheck = await lookupService.basicPhoneNumberCheck(phoneNumber);

    return basicPhoneCheck && basicPhoneCheck.isValid;
  }

  private telephoneContainsLetters(phoneNumber: string): boolean {
    return /[a-zA-Z]/.test(phoneNumber);
  }

  private phoneNumberIsInvalidLength(phoneNumber: string) : boolean {
    const e164Format = this.telephoneToE164Format(phoneNumber);
    return e164Format.length < 12 || e164Format.length > 13;
  }

  private telephoneToE164Format(phoneNumber: string) : string {
    // remove non-digits from the number
    let e164TelephoneNumber = phoneNumber.replace(/[^0-9]/g, '');

    const telephonePrefixes = ['44', '440', '0'];

    const matchedPrefix = telephonePrefixes.find((prefix) => e164TelephoneNumber.startsWith(prefix));

    if (matchedPrefix) {
      e164TelephoneNumber = e164TelephoneNumber.replace(matchedPrefix, '+44');
    }

    return e164TelephoneNumber;
  }

  public async sendVerificationCode(phoneNumber: string): Promise<IResponseModel<ValidationResultModel>> {
    if (phoneNumber == null || phoneNumber.length < 1) {
      return {
        success: false,
        result: {
          result: ValidationResultType.Error,
          messages: ['Invalid phone number.']
        }
      };
    }

    const phone = encodeURIComponent(phoneNumber);

    return fetch(`${this.umbracoValidationApiDomain}/verification/phoneNumber?phoneNumber=${phone}`)
      .then((response: any) => response.json())
      .then((json: any) => json as IResponseModel<ValidationResultModel>)
      .catch(() => {
        const result: IResponseModel<ValidationResultModel> = {
          success: false,
          result: {
            result: ValidationResultType.Error,
            messages: ['Failed to send verification message.']
          }
        };

        return result;
      });
  }

  public async checkVerificationCode(verifyToken: string, phoneNumber: string): Promise<IResponseModel<ValidationResultModel>> {
    if (phoneNumber == null || phoneNumber.length < 1 || verifyToken == null || verifyToken.length < 1) {
      return {
        success: false,
        result: {
          result: ValidationResultType.Error,
          messages: ['Invalid phone number or code.']
        }
      };
    }

    const phone = encodeURIComponent(phoneNumber);

    return fetch(`${this.umbracoValidationApiDomain}/verification/verifyCode?verifyToken=${verifyToken}&phoneNumber=${phone}`)
      .then((response: any) => response.json())
      .then((json: any) => json as IResponseModel<ValidationResultModel>)
      .catch(() => {
        const result: IResponseModel<ValidationResultModel> = {
          success: false,
          result: {
            result: ValidationResultType.InvalidCode,
            messages: ['Incorrect code entered.']
          }
        };

        return result;
      });
  }

  public async isEmailValid(email: string, useEnhancedValidation: boolean = false): Promise<boolean> {
    if (!this.isEmailAddressFormatValid(email)) {
      return false;
    }

    const validationLevel: ValidationLevelType = useEnhancedValidation
      ? ValidationLevelType.Complete
      : ValidationLevelType.Simple;

    const activeEmailCheckResponse = await lookupService.activeEmailCheck(email, validationLevel);

    if (activeEmailCheckResponse && activeEmailCheckResponse.isValid) {
      return true;
    }

    return false;
  }

  public isEmailAddressFormatValid(email: string): boolean {
    const minimumLength = 'a@a.co'.length;
    if (email == null || email.length < minimumLength) {
      return false;
    }

    if (!RegexExpressions.EMAIL.test(email)) {
      return false;
    }

    return true;
  }
}

// export a singleton instance in the global namespace
// eslint-disable-next-line import/prefer-default-export
export const validationApiService = ValidationApiService.instance;
